import React, { useState, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";

import {
  MapPin,
  Check,
  Image as ImageIcon,
  Plus,
  X,
  Loader2,
  AlertCircle,
  Clock,
  Flag,
  Bed,
  CheckCircle2,
  UploadCloud,
  Pencil,
  Trash2,
  PackagePlus,
  ArrowLeft,
  Copy,
  GripVertical,
} from "lucide-react";
import { useDragReorder } from "@/lib/useDragReorder";
import { saveItemOrder } from "@/services/reorderService";
import { IMAGE_ACCEPT, isImageFile } from "@/lib/uploadImage";

/**
 * Validation — mirrors what the backend/HOME_PACKAGES consumer (PackageDetail)
 * expects. Keep in sync by hand if that shape ever changes.
 */
function validatePackageForm(form) {
  const errors = {};

  if (!form.destination.trim()) errors.destination = "Destination is required.";

  const tagline = form.tagline.trim();
  if (!tagline) errors.tagline = "Tagline is required.";
  else if (tagline.length > 200)
    errors.tagline = "Tagline must be under 200 characters.";

  if (!form.duration.trim())
    errors.duration = 'Duration is required, e.g. "5 Days / 4 Nights".';

  // Custom packages don't fix a round count — the golfer picks how many on the
  // package page — so there's nothing to validate here.
  if (!form.custom) {
    const rounds = Number(form.rounds);
    if (form.rounds === "" || Number.isNaN(rounds) || rounds <= 0) {
      errors.rounds = "Rounds must be a number greater than 0.";
    }
  }

  const nights = Number(form.nights);
  if (form.nights === "" || Number.isNaN(nights) || nights < 0) {
    // 0 is valid — day-trip packages have no hotel stay included, and can
    // optionally add one at checkout (see RecommendedTravelStep).
    errors.nights = "Nights must be a number, 0 or greater.";
  }

  const price = Number(form.price);
  if (form.price === "") errors.price = "Price is required.";
  else if (Number.isNaN(price)) errors.price = "Price must be a number.";
  else if (price <= 0) errors.price = "Price must be greater than 0.";
  else if (price > 200000)
    errors.price =
      "Price looks wrong — over SGD 200,000 per person. Double-check this.";

  if (!form.image.trim()) errors.image = "A hero image is required.";

  const emptyIncluded = form.included.some((i) => !i.trim());
  if (form.included.length === 0) {
    errors.included = "Add at least one thing that's included.";
  } else if (emptyIncluded) {
    errors.included = "Remove empty entries before saving.";
  }
  return errors;
}

const emptyForm = {
  destination: "",
  tagline: "",
  duration: "",
  rounds: "",
  nights: "",
  price: "",
  image: "",
  included: [],
  // When true, this is a "build your own" package: the round count isn't fixed
  // here — the golfer chooses how many rounds on the package page.
  custom: false,
};

/**
 * Maps a real course doc (from courseService.getAllCourses) into the
 * shape this form's "golf club" picker cards expect.
 */
function packageToCourseCard(pkg) {
  return {
    id: pkg.id,
    name: pkg.name,
    image: pkg.image,
    destination: pkg.destination,
    tags: (pkg.included || []).slice(0, 3),
  };
}

/**
 * Reconstructs form state from an existing gateway doc for edit mode.
 */
function gatewayToForm(gateway) {
  return {
    destination: gateway.destination || "",
    tagline: gateway.tagline || "",
    duration: gateway.duration || "",
    rounds: gateway.rounds != null ? String(gateway.rounds) : "",
    nights: gateway.nights != null ? String(gateway.nights) : "",
    price: gateway.price != null ? String(gateway.price) : "",
    image: gateway.image || "",
    included: gateway.included || [],
    custom: !!gateway.custom,
  };
}

async function downloadImage(url, filename = "image.jpg") {
  try {
    const response = await fetch(url, { mode: "cors" });
    if (!response.ok) throw new Error("Failed to fetch image");
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(blobUrl);
  } catch (err) {
    console.error("Download failed, opening image in a new tab instead:", err);
    window.open(url, "_blank", "noopener,noreferrer");
  }
}

function filenameFromUrl(url, fallback) {
  try {
    const path = decodeURIComponent(new URL(url).pathname);
    const last = path.split("/").pop();
    return last || fallback;
  } catch {
    return fallback;
  }
}

/**
 * @param {{
 *   gateways: Array<Record<string, any>>;
 *   golfPackages: Array<Record<string, any>>;
 *   onSubmit: (payload: any) => Promise<any>;
 *   onUpdate: (id: string, payload: any) => Promise<any>;
 *   onDelete: (id: string, imageUrl?: string) => Promise<any>;
 *   onCancel?: () => void;
 *   onUploadImage: (file: File, existingUrl?: string, editingId?: string|null) => Promise<string>;
 * }} props
 */
export default function CreateGatewayAdmin({
  gateways = [],
  golfPackages = [],
  onSubmit,
  onUpdate,
  onDelete,
  onCancel,
  onUploadImage,
}) {
  // Manage is the default landing view — see create-course.jsx for why.
  const [view, setView] = useState("manage"); // "form" | "manage"

  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [listError, setListError] = useState(null);

  const formTopRef = useRef(null);

  const [newIncluded, setNewIncluded] = useState("");

  const courseCards = golfPackages.map(packageToCourseCard);

  const [duplicatingId, setDuplicatingId] = useState(null);

  function stripUndefined(value) {
    if (Array.isArray(value)) return value.map(stripUndefined);
    if (value && typeof value === "object") {
      return Object.fromEntries(
        Object.entries(value)
          .filter(([, v]) => v !== undefined)
          .map(([k, v]) => [k, stripUndefined(v)]),
      );
    }
    return value;
  }

  const toggleCourseSelection = (course) => {
    setSelectedCourses((prev) => {
      const exists = prev.some((c) => c.id === course.id);
      return exists
        ? prev.filter((c) => c.id !== course.id)
        : [...prev, course];
    });
  };

  const setField = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const markTouched = (field) =>
    setTouched((prev) => ({ ...prev, [field]: true }));

  const addToList = (field, value, setValue) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    setField(field, [...form[field], trimmed]);
    setValue("");
  };
  const removeFromList = (field, index) =>
    setField(
      field,
      form[field].filter((_, i) => i !== index),
    );

  const resetForm = () => {
    setForm(emptyForm);
    setErrors({});
    setTouched({});
    setSelectedCourses([]);
    setEditingId(null);
    setSubmitError(null);
    setSubmitSuccess(false);
  };

  const startCreate = () => {
    resetForm();
    setView("form");
  };

  const startEdit = (gateway) => {
    setForm(gatewayToForm(gateway));
    setSelectedCourses(normalizeSelectedCourses(gateway.selectedCourses));
    setErrors({});
    setTouched({});
    setSubmitError(null);
    setSubmitSuccess(false);
    setEditingId(gateway.id);
    setView("form");
    formTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  function normalizeSelectedCourses(raw) {
    if (Array.isArray(raw)) return raw;
    if (raw && typeof raw === "object") {
      const seen = new Set();
      const flat = [];
      Object.values(raw).forEach((arr) => {
        (arr || []).forEach((c) => {
          if (c && !seen.has(c.id)) {
            seen.add(c.id);
            flat.push(c);
          }
        });
      });
      return flat;
    }
    return [];
  }

  const handleDelete = async (gateway) => {
    setListError(null);
    setDeletingId(gateway.id);
    try {
      await onDelete(gateway.id, gateway.image);
      if (editingId === gateway.id) resetForm();
    } catch (err) {
      console.error("Failed to delete gateway:", err);
      setListError(
        err?.message || "Failed to delete gateway. Please try again.",
      );
    } finally {
      setDeletingId(null);
      setConfirmDeleteId(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);
    setSubmitSuccess(false);

    const validationErrors = validatePackageForm(form);
    setErrors(validationErrors);
    setTouched(
      Object.keys(form).reduce((acc, key) => ({ ...acc, [key]: true }), {}),
    );

    if (Object.keys(validationErrors).length > 0) return;
    const payload = stripUndefined({
      destination: form.destination.trim(),
      tagline: form.tagline.trim(),
      duration: form.duration.trim(),
      custom: form.custom,
      // Custom packages carry no fixed round count — null it out so switching
      // an existing package to custom clears the old value (updateGateway merges).
      rounds: form.custom ? null : Number(form.rounds),
      nights: Number(form.nights),
      price: Number(form.price),
      image: form.image.trim(),
      included: form.included.map((i) => i.trim()).filter(Boolean),
      selectedCourses, // flat array now
    });

    setIsSubmitting(true);
    try {
      if (editingId) {
        await onUpdate(editingId, payload);
      } else {
        await onSubmit(payload);
      }
      setSubmitSuccess(true);
      resetForm();
      setView("manage");
    } catch (err) {
      console.error("Failed to save gateway:", err);
      setSubmitError(
        err?.message || "Failed to save gateway. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDuplicate = async (gateway) => {
    setListError(null);
    setDuplicatingId(gateway.id);
    try {
      // Strip fields that must be unique/regenerated per-doc — id, createdAt,
      // updatedAt come from Firestore/service layer, not from the source doc.
      const { id, createdAt, updatedAt, ...rest } = gateway;
      const payload = stripUndefined({
        ...rest,
        destination: `${gateway.destination} (Copy)`,
      });
      await onSubmit(payload); // onSubmit already calls generateGatewayId()+createGateway() with a fresh id
    } catch (err) {
      console.error("Failed to duplicate gateway:", err);
      setListError(
        err?.message || "Failed to duplicate gateway. Please try again.",
      );
    } finally {
      setDuplicatingId(null);
    }
  };

  const fieldError = (field) =>
    touched[field] && errors[field] ? errors[field] : null;

  return (
    <div className="min-h-screen bg-[#F7F8F7] py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          ref={formTopRef}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between w-full mb-2">
            <div className="flex items-center gap-3">
              {view === "form" && editingId && (
                <button
                  type="button"
                  onClick={() => {
                    resetForm();
                    setView("manage");
                  }}
                  className="h-9 w-9 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-50"
                >
                  <ArrowLeft className="h-4 w-4" />
                </button>
              )}
              <h1 className="text-3xl font-bold text-[#0B2421]">
                {view === "manage"
                  ? "Manage Gateways"
                  : editingId
                    ? "Edit Gateway"
                    : "Add a Gateway"}
              </h1>
            </div>

            {view === "form" ? (
              <button
                type="button"
                onClick={() => setView("manage")}
                className="h-12 px-6 rounded-xl border border-[#0B2421]/20 text-[#0B2421] font-semibold hover:bg-[#0B2421]/5 transition-colors"
              >
                Manage Gateways
              </button>
            ) : (
              <button
                type="button"
                onClick={startCreate}
                className="h-12 px-6 rounded-xl bg-[#0B2421] hover:bg-[#0B2421]/85 text-white font-semibold transition-colors flex items-center gap-2"
              >
                <PackagePlus className="h-4 w-4" />
                New Gateway
              </button>
            )}
          </div>
          <p className="text-gray-500 mt-1">
            {view === "manage"
              ? "Edit or remove existing gateways."
              : "Fields marked with an asterisk are required before this can go live."}
          </p>
        </motion.div>

        {view === "manage" ? (
          <ManageView
            gateways={gateways}
            error={listError}
            onEdit={startEdit}
            onDuplicate={handleDuplicate}
            duplicatingId={duplicatingId}
            onDeleteRequest={setConfirmDeleteId}
            confirmDeleteId={confirmDeleteId}
            onDeleteCancel={() => setConfirmDeleteId(null)}
            onDeleteConfirm={handleDelete}
            deletingId={deletingId}
          />
        ) : (
          <>
            {submitSuccess && (
              <div className="mb-6 flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-emerald-800">
                Gateway saved successfully.
              </div>
            )}
            {submitError && (
              <div className="mb-6 flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span>{submitError}</span>
              </div>
            )}

            <form id="gateway-form" onSubmit={handleSubmit} noValidate>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
                <div className="lg:col-span-2 space-y-6">
                  <Section title="Hero Image" icon={ImageIcon}>
                    <FieldLabel required>Photo</FieldLabel>
                    <MainImageUpload
                      value={form.image}
                      onChange={(url) => setField("image", url)}
                      onBlur={() => markTouched("image")}
                      onUploadImage={(file) =>
                        onUploadImage(file, form.image, editingId)
                      }
                      error={fieldError("image")}
                    />
                  </Section>

                  <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.05 }}
                    className="p-6 bg-white border border-gray-100 rounded-2xl shadow-sm"
                  >
                    <div className="flex items-start justify-between gap-4 mb-4">
                      <h2 className="text-lg font-bold text-[#0B2421] flex items-center">
                        <Flag className="h-5 w-5 mr-2" />
                        Golf Club Pool
                      </h2>
                      <label className="flex items-center gap-2 cursor-pointer select-none shrink-0">
                        <input
                          type="checkbox"
                          checked={form.custom}
                          onChange={(e) => setField("custom", e.target.checked)}
                          className="h-4 w-4 rounded border-gray-300 text-[#0B2421] focus:ring-[#0B2421]/30"
                        />
                        <span className="text-sm font-semibold text-[#0B2421]">
                          Custom package
                        </span>
                      </label>
                    </div>
                    <p className="text-sm text-gray-400 mb-4">
                      Customers will choose from these clubs independently for
                      each round.
                      {form.custom &&
                        " Custom package: golfers pick how many rounds to play on the package page."}
                    </p>

                    {!form.custom && (
                      <div className="mb-5 max-w-xs">
                        <Field
                          label="Total Rounds of Golf"
                          required
                          icon={Flag}
                          error={fieldError("rounds")}
                        >
                          <input
                            type="number"
                            min="1"
                            value={form.rounds}
                            onChange={(e) => setField("rounds", e.target.value)}
                            onBlur={() => markTouched("rounds")}
                            placeholder="4"
                            className={inputClass(fieldError("rounds"))}
                          />
                        </Field>
                      </div>
                    )}

                    {courseCards.length === 0 ? (
                      <p className="text-sm text-gray-400 mb-2">
                        No courses available yet — create one first so it can be
                        assigned here.
                      </p>
                    ) : (
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                        {courseCards.map((course) => {
                          const isSelected = selectedCourses.some(
                            (c) => c.id === course.id,
                          );
                          return (
                            <div
                              key={course.id}
                              onClick={() => toggleCourseSelection(course)}
                              className={`cursor-pointer rounded-2xl overflow-hidden border-2 transition-all shadow-sm hover:shadow-md ${
                                isSelected
                                  ? "border-[#0B2421]"
                                  : "border-gray-100 hover:border-gray-300"
                              }`}
                            >
                              <div className="relative h-32 overflow-hidden bg-gray-100">
                                {course.image ? (
                                  <img
                                    src={course.image}
                                    alt={course.name}
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center text-gray-300">
                                    <ImageIcon className="h-6 w-6" />
                                  </div>
                                )}
                                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                                <h3 className="absolute bottom-3 left-3 text-white font-bold text-sm leading-snug pr-10">
                                  {course.name}
                                </h3>
                                {isSelected && (
                                  <div className="absolute top-3 right-3 w-7 h-7 bg-[#0B2421] rounded-full flex items-center justify-center">
                                    <Check className="w-4 h-4 text-[#D4AF37]" />
                                  </div>
                                )}
                              </div>
                              <div className="p-3 bg-white">
                                {course.destination && (
                                  <span className="flex items-center gap-1 text-xs text-gray-500 mb-2">
                                    <MapPin className="w-3 h-3" />
                                    {course.destination}
                                  </span>
                                )}
                                {course.tags.length > 0 && (
                                  <div className="flex flex-wrap gap-1 mb-2">
                                    {course.tags.map((tag) => (
                                      <span
                                        key={tag}
                                        className="text-[10px] bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full"
                                      >
                                        {tag}
                                      </span>
                                    ))}
                                  </div>
                                )}
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleCourseSelection(course);
                                  }}
                                  className={`w-full px-4 py-2 rounded-lg text-xs font-bold transition-colors ${
                                    isSelected
                                      ? "bg-emerald-700 text-white"
                                      : "bg-[#0B2421] text-white hover:bg-[#0B2421]/85"
                                  }`}
                                >
                                  {isSelected ? "Selected" : "Select"}
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </motion.div>

                  <Section title="Basics">
                    <div className="space-y-4">
                      <Field
                        label="Destination"
                        required
                        icon={MapPin}
                        error={fieldError("destination")}
                      >
                        <input
                          type="text"
                          value={form.destination}
                          onChange={(e) =>
                            setField("destination", e.target.value)
                          }
                          onBlur={() => markTouched("destination")}
                          placeholder="e.g., St Andrews, Scotland"
                          className={inputClass(fieldError("destination"))}
                        />
                      </Field>

                      <Field
                        label="Tagline"
                        required
                        error={fieldError("tagline")}
                      >
                        <input
                          type="text"
                          value={form.tagline}
                          onChange={(e) => setField("tagline", e.target.value)}
                          onBlur={() => markTouched("tagline")}
                          placeholder="A short line shown under the title on the hero"
                          className={inputClass(fieldError("tagline"))}
                        />
                      </Field>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Field
                          label="Duration"
                          required
                          icon={Clock}
                          error={fieldError("duration")}
                        >
                          <input
                            type="text"
                            value={form.duration}
                            onChange={(e) =>
                              setField("duration", e.target.value)
                            }
                            onBlur={() => markTouched("duration")}
                            placeholder="5 Days / 4 Nights"
                            className={inputClass(fieldError("duration"))}
                          />
                        </Field>

                        <Field
                          label="Nights"
                          required
                          icon={Bed}
                          error={fieldError("nights")}
                        >
                          <input
                            type="number"
                            min="0"
                            value={form.nights}
                            onChange={(e) => setField("nights", e.target.value)}
                            onBlur={() => markTouched("nights")}
                            placeholder="0 for a day trip, or 4"
                            className={inputClass(fieldError("nights"))}
                          />
                        </Field>
                      </div>

                      <Field
                        label="Price per Person"
                        required
                        error={fieldError("price")}
                      >
                        <div className="relative">
                          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#D4AF37] font-semibold">
                            SGD
                          </span>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={form.price}
                            onChange={(e) => setField("price", e.target.value)}
                            onBlur={() => markTouched("price")}
                            placeholder="2500"
                            className={`${inputClass(fieldError("price"))} pl-16`}
                          />
                        </div>
                      </Field>
                    </div>
                  </Section>

                  <Section title="What's Included" icon={CheckCircle2}>
                    <ChipListEditor
                      items={form.included}
                      inputValue={newIncluded}
                      setInputValue={setNewIncluded}
                      onAdd={() =>
                        addToList("included", newIncluded, setNewIncluded)
                      }
                      onRemove={(i) => removeFromList("included", i)}
                      placeholder="e.g. 1 Round, 18 Holes of Golf"
                    />
                    {fieldError("included") && (
                      <FieldError message={errors.included} />
                    )}
                  </Section>

                  <div className="flex items-center gap-3 justify-end pb-4">
                    {editingId && (
                      <button
                        type="button"
                        onClick={() => {
                          resetForm();
                          setView("manage");
                        }}
                        disabled={isSubmitting}
                        className="h-12 px-6 rounded-xl border border-gray-200 text-gray-600 font-medium hover:bg-gray-50 transition-colors disabled:opacity-40"
                      >
                        Cancel Edit
                      </button>
                    )}

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="h-12 px-8 rounded-xl bg-[#0B2421] hover:bg-[#0B2421]/85 text-white font-semibold transition-colors disabled:opacity-40 flex items-center gap-2"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          {editingId ? "Updating..." : "Creating..."}
                        </>
                      ) : editingId ? (
                        "Update Gateway"
                      ) : (
                        "Create Gateway"
                      )}
                    </button>
                  </div>
                </div>

                <div className="hidden lg:block lg:sticky lg:top-8">
                  <LivePreview form={form} />
                </div>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

/* ---------- Manage / list view ---------- */

function ManageView({
  gateways,
  error,
  onEdit,
  onDuplicate,
  duplicatingId,
  onDeleteRequest,
  confirmDeleteId,
  onDeleteCancel,
  onDeleteConfirm,
  deletingId,
}) {
  const deleteTarget = gateways.find((g) => g.id === confirmDeleteId) || null;

  // Manual drag order (persisted via saveItemOrder) replaces what used to
  // be a fixed sort-by-rounds-count — the admin now controls display
  // order directly instead of it being derived from package size.
  const {
    items: sortedGateways,
    getDragHandlers,
    isDragging,
    isDragOver,
  } = useDragReorder(
    gateways,
    (g) => g.id,
    (ids) => saveItemOrder("getaway", ids),
  );

  if (error) {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700">
        <AlertCircle className="h-4 w-4 flex-shrink-0" />
        <span>{error}</span>
      </div>
    );
  }

  if (gateways.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-gray-200 bg-white p-12 text-center">
        <PackagePlus className="h-8 w-8 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500">No gateways yet.</p>
      </div>
    );
  }

  return (
    <>
      <p className="text-xs text-gray-400 mb-3 flex items-center gap-1.5">
        <GripVertical className="h-3.5 w-3.5" />
        Drag a card to reorder — this is the order shown to golfers.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
       {sortedGateways.map((gateway) => (
          <div
            key={gateway.id}
            {...getDragHandlers(gateway)}
            className={`rounded-2xl border bg-white shadow-sm overflow-hidden group cursor-grab active:cursor-grabbing transition-opacity ${
              isDragging(gateway)
                ? "opacity-40 border-gray-100"
                : isDragOver(gateway)
                  ? "border-[#0B2421] ring-2 ring-[#0B2421]/30"
                  : "border-gray-100"
            }`}
          >
            <div className="relative h-36 bg-gray-100">
              <div className="absolute top-2 left-2 z-10 h-6 w-6 rounded-md bg-black/40 backdrop-blur-sm flex items-center justify-center text-white/80">
                <GripVertical className="h-3.5 w-3.5" />
              </div>
              {gateway.image ? (
                <img
                  src={gateway.image}
                  alt=""
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-300">
                  <ImageIcon className="h-6 w-6" />
                </div>
              )}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                <button
                  type="button"
                  onClick={() => onEdit(gateway)}
                  className="h-9 px-4 rounded-lg bg-white text-[#0B2421] text-sm font-semibold flex items-center gap-1.5"
                >
                  <Pencil className="h-3.5 w-3.5" />
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() => onDuplicate(gateway)}
                  disabled={duplicatingId === gateway.id}
                  className="h-9 px-4 rounded-lg bg-white text-[#0B2421] text-sm font-semibold flex items-center gap-1.5 disabled:opacity-40"
                >
                  {duplicatingId === gateway.id ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Copy className="h-3.5 w-3.5" />
                  )}
                  Duplicate
                </button>
                <button
                  type="button"
                  onClick={() => onDeleteRequest(gateway.id)}
                  className="h-9 px-4 rounded-lg bg-white/90 text-red-600 text-sm font-semibold flex items-center gap-1.5"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Delete
                </button>
              </div>
            </div>
            <div className="p-4">
              {gateway.destination && (
                <p className="text-[10px] font-semibold uppercase tracking-widest text-[#D4AF37] mb-0.5">
                  {gateway.destination}
                </p>
              )}
              <h3 className="text-sm font-bold text-[#0B2421] truncate">
                {gateway.destination}
              </h3>
              <div className="flex items-center justify-between mt-2">
                <span className="text-xs text-gray-500 truncate pr-2">
                  {gateway.duration}
                </span>
                {gateway.price != null && (
                  <span className="text-sm font-bold text-[#0B2421] flex-shrink-0">
                    SGD {gateway.price}
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <AnimatePresence>
        {deleteTarget && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
            onClick={onDeleteCancel}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl p-6 max-w-sm w-full"
            >
              <h3 className="text-lg font-bold text-[#0B2421] mb-2">
                Delete gateway?
              </h3>
              <p className="text-sm text-gray-500 mb-6">
                This will permanently remove "{deleteTarget.destination}" and
                its hero image. This can't be undone.
              </p>
              <div className="flex items-center gap-3 justify-end">
                <button
                  type="button"
                  onClick={onDeleteCancel}
                  disabled={deletingId === deleteTarget.id}
                  className="h-10 px-4 rounded-xl border border-gray-200 text-gray-600 font-medium hover:bg-gray-50 disabled:opacity-40"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => onDeleteConfirm(deleteTarget)}
                  disabled={deletingId === deleteTarget.id}
                  className="h-10 px-4 rounded-xl bg-red-600 hover:bg-red-700 text-white font-semibold disabled:opacity-40 flex items-center gap-2"
                >
                  {deletingId === deleteTarget.id ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    "Delete"
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

/* ---------- layout primitives ---------- */

function Section({ title, icon: Icon, children }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="p-6 bg-white border border-gray-100 rounded-2xl shadow-sm"
    >
      <h2 className="text-lg font-bold mb-4 text-[#0B2421] flex items-center">
        {Icon && <Icon className="h-5 w-5 mr-2" />}
        {title}
      </h2>
      {children}
    </motion.div>
  );
}

function FieldLabel({ children, required, icon: Icon }) {
  return (
    <label className="text-sm font-semibold text-[#0B2421] mb-2 block">
      {Icon && <Icon className="inline h-4 w-4 mr-1" />}
      {children} {required && <span className="text-[#D4AF37]">*</span>}
    </label>
  );
}

function Field({ label, required, icon, error, children }) {
  return (
    <div>
      <FieldLabel required={required} icon={icon}>
        {label}
      </FieldLabel>
      {children}
      {error && <FieldError message={error} />}
    </div>
  );
}

function inputClass(hasError) {
  return `w-full h-12 rounded-xl border px-4 text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#0B2421]/30 ${
    hasError ? "border-red-300 bg-red-50" : "border-gray-200 bg-gray-50"
  }`;
}

function FieldError({ message }) {
  return (
    <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
      <AlertCircle className="h-3 w-3 flex-shrink-0" />
      {message}
    </p>
  );
}

/* ---------- image upload ---------- */

function MainImageUpload({ value, onChange, onBlur, onUploadImage, error }) {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [localError, setLocalError] = useState(null);
  const inputRef = useRef(null);

  const handleFile = useCallback(
    async (file) => {
      if (!file || !isImageFile(file)) {
        setLocalError("Please choose an image file.");
        return;
      }
      setLocalError(null);
      setIsUploading(true);
      try {
        const url = await onUploadImage(file);
        onChange(url);
      } catch (err) {
        console.error("Image upload failed:", err);
        setLocalError(err?.message || "Upload failed. Try again.");
      } finally {
        setIsUploading(false);
        onBlur?.();
      }
    },
    [onUploadImage, onChange, onBlur],
  );

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleFile(e.dataTransfer.files?.[0]);
  };

  if (value && !isUploading) {
    return (
      <div className="relative group rounded-xl overflow-hidden border border-gray-200 h-56">
        <img
          src={value}
          alt="Package hero preview"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
          <button
            type="button"
            onClick={() =>
              downloadImage(value, filenameFromUrl(value, "main-photo.jpg"))
            }
            className="px-4 py-2 rounded-lg bg-white/90 text-green-600 text-sm font-semibold"
          >
            Download
          </button>
          
          
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="px-4 py-2 rounded-lg bg-white text-[#0B2421] text-sm font-semibold"
          >
            Replace
          </button>
          <button
            type="button"
            onClick={() => onChange("")}
            className="px-4 py-2 rounded-lg bg-white/90 text-red-600 text-sm font-semibold"
          >
            Remove
          </button>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={IMAGE_ACCEPT}
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
      </div>
    );
  }

  return (
    <div>
      <label
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        className={`flex flex-col items-center justify-center h-56 rounded-xl border-2 border-dashed cursor-pointer transition-colors ${
          isDragging
            ? "border-[#0B2421] bg-[#0B2421]/5"
            : error || localError
              ? "border-red-300 bg-red-50"
              : "border-gray-200 bg-gray-50 hover:bg-gray-100"
        }`}
      >
        {isUploading ? (
          <>
            <Loader2 className="h-6 w-6 text-[#0B2421] animate-spin mb-2" />
            <span className="text-sm text-gray-500">Uploading...</span>
          </>
        ) : (
          <>
            <UploadCloud className="h-6 w-6 text-gray-400 mb-2" />
            <span className="text-sm font-medium text-gray-600">
              Click to upload or drag a photo here
            </span>
            <span className="text-xs text-gray-400 mt-1">
              This shows as the hero banner on the package page
            </span>
          </>
        )}
        <input
          type="file"
          accept={IMAGE_ACCEPT}
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
      </label>
      {(error || localError) && <FieldError message={error || localError} />}
    </div>
  );
}

/* ---------- chip list ---------- */

function ChipListEditor({
  items,
  inputValue,
  setInputValue,
  onAdd,
  onRemove,
  placeholder,
}) {
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      onAdd();
    }
  };

  return (
    <div>
      <div className="flex gap-2 mb-3">
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="flex-1 h-11 rounded-xl border border-gray-200 bg-gray-50 px-4 text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#0B2421]/30"
        />
        <button
          type="button"
          onClick={onAdd}
          className="h-11 w-11 rounded-xl bg-[#0B2421] hover:bg-[#0B2421]/85 text-white flex items-center justify-center flex-shrink-0 transition-colors"
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>
      {items.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {items.map((item, index) => (
            <span
              key={`${item}-${index}`}
              className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1.5 text-sm text-gray-700"
            >
              {item}
              <button
                type="button"
                onClick={() => onRemove(index)}
                className="text-gray-400 hover:text-red-500 transition-colors"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- live preview ---------- */

function LivePreview({ form }) {
  const hasContent = form.destination || form.image || form.tagline;

  return (
    <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100 bg-[#0B2421]">
        <span className="text-xs font-semibold tracking-wide text-[#D4AF37] uppercase">
          Preview
        </span>
      </div>

      {!hasContent ? (
        <div className="p-8 text-center text-sm text-gray-400">
          Fill in the form to preview this gateway here.
        </div>
      ) : (
        <div>
          <div className="relative h-40 bg-gray-100">
            {form.image ? (
              <img
                src={form.image}
                alt=""
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-300">
                <ImageIcon className="h-8 w-8" />
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
            <div className="absolute bottom-3 left-4 right-4">
              {form.destination && (
                <p className="text-[#D4AF37] text-[10px] font-semibold uppercase tracking-widest mb-0.5">
                  {form.destination}
                </p>
              )}
              <h3 className="text-white font-bold text-base leading-snug">
                {form.destination || "Destination"}
              </h3>
            </div>
          </div>

          <div className="p-5">
            {form.tagline && (
              <p className="text-sm text-gray-500 mb-3">{form.tagline}</p>
            )}

            <div className="flex flex-wrap gap-3 mb-4">
              {form.duration && (
                <span className="flex items-center gap-1 text-xs text-gray-600">
                  <Clock className="h-3 w-3" />
                  {form.duration}
                </span>
              )}
              {form.custom ? (
                <span className="flex items-center gap-1 text-xs text-gray-600">
                  <Flag className="h-3 w-3" />
                  Custom rounds
                </span>
              ) : (
                form.rounds && (
                  <span className="flex items-center gap-1 text-xs text-gray-600">
                    <Flag className="h-3 w-3" />
                    {form.rounds} Rounds
                  </span>
                )
              )}
              {form.nights && (
                <span className="flex items-center gap-1 text-xs text-gray-600">
                  <Bed className="h-3 w-3" />
                  {form.nights} Nights
                </span>
              )}
            </div>

            {form.price && (
              <div className="flex items-baseline gap-1 mb-4">
                <span className="text-[#0B2421] font-bold text-2xl">
                  SGD {form.price}
                </span>
                <span className="text-gray-400 text-sm">Per Person</span>
              </div>
            )}

            {form.included.length > 0 && (
              <div className="space-y-1.5 mb-4">
                {form.included.slice(0, 4).map((item, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <CheckCircle2 className="h-3.5 w-3.5 text-[#D4AF37] flex-shrink-0" />
                    <span className="text-xs text-gray-600">{item}</span>
                  </div>
                ))}
                {form.included.length > 4 && (
                  <span className="text-xs text-gray-400 pl-5">
                    +{form.included.length - 4} more
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
